import { DatePipe, DecimalPipe } from '@angular/common';
import { inject, Injectable, Pipe, PipeTransform } from '@angular/core';
import { AppService } from '@app/app.service';
import * as moment from 'moment';

/**
 * Форматер для данных
 */
@Pipe({ name: 'formater' })
@Injectable()
export class FormaterPipe implements PipeTransform {
    public readonly appService: AppService = inject(AppService);
    public readonly decimalPipe: DecimalPipe = inject(DecimalPipe);
    public readonly datePipe: DatePipe = inject(DatePipe);

    /**
     * Значение по умолчанию
     */
    valueDefault: string = '';

    /** Параметр для трансформации */
    transformParam: any;

    /**
     * Обработка данных
     * @param input данные
     * @param type форматер
     * @param valueDefault Значение по умолчанию
     * @param param параметр для трансформации
     */
    transform(input: any, type: string, valueDefault: string = '', param: any = null) {
        this.valueDefault = valueDefault;
        this.transformParam = param;
        type = this.snakeToCamel(type);
        if (this[type]) {
            return this[type](input)?.replace(/<[^>]*>/g, '');
        } else {
            return this.string(input)?.replace(/<[^>]*>/g, '');
        }
    }

    /**
     * Форматер дробного числа
     */
    float(input: any) {
        try {
            this.transformParam = this.transformParam ?? 2;
            if (input) {
                const array = input.split(', ');
                if (array && array.length > 1) {
                    const result = [];
                    array.forEach((value) => {
                        if (value) {
                            result.push(
                                this.decimalPipe.transform(
                                    value,
                                    '1.' + this.transformParam + '-' + this.transformParam,
                                    this.appService.currentLangCode,
                                ),
                            );
                        }
                    });
                    return result.join(' | ');
                }
            }
            if (input) {
                input = this.decimalPipe.transform(
                    input,
                    '1.' + this.transformParam + '-' + this.transformParam,
                    this.appService.currentLangCode,
                );
                return input;
            } else {
                return this.valueDefault;
            }
        } catch (e) {
            return input ?? this.valueDefault;
        }
    }

    /**
     * Форматер дробного числа
     */
    digit(input: any) {
        return this.float(input);
    }

    /**
     * Форматер текста
     */
    text(input: any) {
        return this.string(input);
    }

    /**
     * Форматер списка
     */
    list(input: any) {
        return this.string(input);
    }

    /**
     * Форматер галлереи
     */
    gallery() {
        return this.valueDefault;
    }

    /**
     * Форматер файла
     */
    file() {
        return this.valueDefault;
    }

    /**
     * Форматер файла с версиями
     */
    fileVersions() {
        return this.valueDefault;
    }

    /**
     * Форматер нимеатора
     */
    numerator(input: any) {
        return this.string(input);
    }

    /**
     * Форматер адреса
     */
    address(input: any) {
        return this.string(input);
    }

    /**
     * Форматер строки
     */
    string(input: any) {
        if (input && typeof input === 'object') {
            if (input.length > 0) {
                return input.join(', ');
            } else {
                return this.valueDefault;
            }
        }
        return input ? String(input) : this.valueDefault;
    }

    /**
     * Форматер логического типа
     */
    boolean(input: any) {
        return this.bool(input);
    }

    /** Форматер логического типа */
    bool(input: any) {
        switch (typeof input) {
            case 'boolean':
                return input
                    ? this.appService.language_dictionary.system_yes
                    : input === false
                      ? this.appService.language_dictionary.system_no
                      : this.appService.language_dictionary.system_not_specified_upp_case;
            case 'number':
                return input === 1
                    ? this.appService.language_dictionary.system_yes
                    : input === 0
                      ? this.appService.language_dictionary.system_no
                      : this.appService.language_dictionary.system_not_specified_upp_case;
            case 'undefined':
            case 'object':
                return this.appService.language_dictionary.system_not_specified_upp_case;
            case 'string':
                if (!input) {
                    return this.appService.language_dictionary.system_not_specified_upp_case;
                }

                const booleans = input.split(',');
                return booleans
                    .map((item) => {
                        if (item.includes('1') || item.includes('true')) {
                            return this.appService.language_dictionary.system_yes;
                        } else if (
                            item.includes(this.appService.language_dictionary.system_yes) ||
                            item.includes(this.appService.language_dictionary.system_no)
                        ) {
                            return item;
                        }

                        return this.appService.language_dictionary.system_no;
                    })
                    .join(', ');
        }
    }

    /**
     * Форматер для чекбокса
     */
    checkbox() {
        return this.valueDefault;
    }

    /**
     * Форматтер для ссылки
     */
    link() {
        return this.valueDefault;
    }

    /**
     * Форматтер для данных рееестра
     */
    reestr() {
        return this.valueDefault;
    }

    /**
     * Форматер целого числа
     */
    integer(input: any) {
        try {
            if (input) {
                const array = input.split(', ');
                if (array && array.length > 1) {
                    const result = [];
                    array.forEach((value) => {
                        if (value) {
                            result.push(this.decimalPipe.transform(value, '1.0-0', this.appService.currentLangCode));
                        }
                    });
                    return result.join(' | ');
                }
            }
            return input
                ? this.decimalPipe.transform(input, '1.0-0', this.appService.currentLangCode)
                : this.valueDefault;
        } catch (e) {
            return this.valueDefault;
        }
    }

    /**
     * Форматер даты
     */
    date(input: any) {
        const p = /^[0-3]\d\.[01]\d\.\d{4}$/;
        if (input && typeof input === 'string' && p.test(input)) {
            return input;
        }
        if (input) {
            const array = input.split(', ');
            if (array && array.length > 1) {
                const result = [];
                array.forEach((value) => {
                    if (input && typeof value === 'string' && p.test(value)) {
                        result.push(value);
                    } else {
                        if (value && moment(value).isValid()) {
                            result.push(this.datePipe.transform(value, 'shortDate'));
                        }
                    }
                });
                return result.length ? result.join(', ') : this.valueDefault !== '' ? this.valueDefault : `${input}`;
            }
        }
        return input && moment(input).isValid()
            ? this.datePipe.transform(input, 'shortDate')
            : this.valueDefault !== ''
              ? this.valueDefault
              : `${input}`;
    }

    /**
     * Форматер для времени и даты
     */
    datetime(input: any) {
        if (input) {
            const array = input.split(', ');
            if (array && array.length > 1) {
                const result = [];
                array.forEach((value) => {
                    if (value && moment(value).isValid()) {
                        result.push(
                            this.datePipe.transform(value, 'shortDate') +
                                ' ' +
                                this.datePipe.transform(value, 'mediumTime'),
                        );
                    }
                });
                return result.join(', ');
            }
        }
        return input && moment(input).isValid()
            ? this.datePipe.transform(input, 'shortDate') + ' ' + this.datePipe.transform(input, 'mediumTime')
            : this.valueDefault;
    }

    /**
     * Дата в особом формате
     * @param input - данные для форматирования
     */
    public dateYmd(input: any) {
        if (!input) {
            return this.valueDefault;
        }
        const date = moment(input);
        return date.isValid() ? date.format('YYYYMMDD') : this.valueDefault;
    }

    /**
     * Преобразовать snake case в camel case
     * @param snakeStr Строка в snake case
     */
    private snakeToCamel(str: string): string {
        if (typeof str !== 'string' || !str.includes('_')) {
            return str;
        }

        return str
            .toLowerCase()
            .split('_')
            .map((word, index) => {
                if (index === 0) {
                    return word;
                }
                return word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join('');
    }
}
